import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Users, Handshake, CalendarHeart, BookOpen, Sparkles } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Container from "@/components/editorial/Container";
import Eyebrow from "@/components/editorial/Eyebrow";
import api from "@/lib/api";
import { getAttribution } from "@/lib/attribution";
import { trackEvent } from "@/lib/analytics";
import { EDITORIAL_IMAGES } from "@/data/content";

const BENEFITS = [
  { icon: Users, title: "Networking curado", desc: "Red de líderes en 12+ industrias y 20+ ciudades." },
  { icon: Handshake, title: "Mentoría 1:1", desc: "Sesiones con mujeres que ya recorrieron el camino." },
  { icon: CalendarHeart, title: "Eventos privados", desc: "Screenings, cenas y paneles solo para miembros." },
  { icon: BookOpen, title: "Directorio editorial", desc: "Visibilidad en directorio de colaboraciones y oportunidades." },
];

const DIRECTORY_CARDS = [
  { title: "Encuentra aliadas", desc: "Busca por industria, ciudad o tipo de colaboración y conecta con mujeres líderes en tu sector." },
  { title: "Ofrece tu expertise", desc: "Publica disponibilidad para mentorías, asesorías o board advisory de forma acotada." },
  { title: "Recibe oportunidades", desc: "Colaboraciones editoriales, alianzas con marcas y eventos curados directamente a tu inbox." },
];

const SITE_URL = "https://mujeres-motor.com";

const INDUSTRIES = [
  "Tecnología", "Manufactura", "Artesanía", "Agricultura",
  "Gastronomía", "Ciencia", "Finanzas", "Salud", "Educación",
  "Medios", "Arte & Cultura", "Otra",
];
const ROLES = ["Miembro", "Mentora", "Aliada de marca", "Otra"];

export default function Community() {
  const [form, setForm] = useState({ name: "", email: "", city: "", industry: "", role_interest: "Miembro", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nombre y email son obligatorios.");
      return;
    }
    setSubmitting(true);
    try {
      const clean = {};
      for (const [k, v] of Object.entries(form)) {
        if (typeof v === "string" && v.trim() !== "") clean[k] = v.trim();
      }
      clean.attribution = getAttribution();
      const { data } = await api.post("/submissions/community", clean);
      trackEvent("form_submit", { form_type: "community" });
      toast.success(data.message || "Bienvenida a la comunidad.");
      setDone(true);
      window.scrollTo({ top: document.getElementById("join")?.offsetTop - 80 || 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SiteLayout transparent>
      <></>

      {/* Hero */}
      <section data-testid="community-hero" className="relative min-h-[60vh] flex items-end bg-charcoal text-cream overflow-hidden pb-16">
        <img src={EDITORIAL_IMAGES.community} alt="Comunidad Mujeres Motor" className="absolute inset-0 w-full h-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/30 via-charcoal/50 to-charcoal" />
        <Container className="relative z-10">
          <Eyebrow tone="gold" className="mb-5 block">Comunidad</Eyebrow>
          <h1 className="font-serif font-light text-[clamp(2.25rem,5vw,4.5rem)] leading-[1.03] max-w-4xl">
            La red de mujeres que <span className="italic">están moviendo México.</span>
          </h1>
          <p className="mt-6 text-cream/75 text-lg max-w-2xl">
            Conexiones reales, mentoría, eventos privados y acceso a la plataforma editorial. Sin costo.
          </p>
        </Container>
      </section>

      {/* Benefits */}
      <section className="bg-cream py-20 md:py-28" data-testid="community-benefits">
        <Container>
          <div className="text-center max-w-xl mx-auto mb-14">
            <Eyebrow tone="terracotta" className="mb-4 block">Por qué unirte</Eyebrow>
            <h2 className="font-serif font-light text-3xl md:text-5xl leading-[1.08]">Lo que se mueve dentro.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map((b) => {
              const Ico = b.icon;
              return (
                <div key={b.title} className="p-7 border border-charcoal/10 rounded-sm bg-white hover:border-terracotta transition-colors duration-[400ms] flex flex-col">
                  <Ico className="h-5 w-5 text-terracotta mb-5" />
                  <h3 className="font-serif text-xl md:text-2xl font-light text-charcoal mb-3 leading-tight">{b.title}</h3>
                  <p className="text-sm text-charcoal/70 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* App teaser */}
      <section className="bg-charcoal text-cream py-24 md:py-28" data-testid="community-app">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <div className="mx-auto max-w-[280px] aspect-[9/19] rounded-[28px] p-5 bg-gradient-to-br from-terracotta via-[#A64826] to-gold border border-cream/10 flex flex-col items-center justify-center text-center shadow-2xl">
                <Sparkles className="h-8 w-8 text-cream mb-5" />
                <p className="text-[10px] uppercase tracking-[0.32em] text-cream/80 mb-3">Coming 2026</p>
                <p className="font-serif italic text-2xl text-cream leading-tight">App Mujeres Motor</p>
                <p className="text-xs text-cream/70 mt-4 px-3">Perfil · Directorio · Eventos · Mentoría IA-asistida</p>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <Eyebrow tone="gold" className="mb-5 block">Próximamente</Eyebrow>
              <h2 className="font-serif font-light text-4xl md:text-5xl mb-8 leading-[1.08]">La app de la comunidad <span className="italic">llega en 2026.</span></h2>
              <ul className="space-y-4 text-cream/80">
                <li className="flex gap-3"><span className="text-gold">·</span>Perfil profesional con directorio por industria y ciudad.</li>
                <li className="flex gap-3"><span className="text-gold">·</span>Agenda de eventos y screenings exclusivos para miembros.</li>
                <li className="flex gap-3"><span className="text-gold">·</span>Matching de mentoría IA-asistida basado en intereses y trayectoria.</li>
              </ul>
              <a href="#join" className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-gold hover:text-cream transition-colors">
                Sé de las primeras en tener acceso <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </Container>
      </section>

      {/* Mentorship */}
      <section className="bg-cream py-24 md:py-28" data-testid="community-mentorship">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <Eyebrow tone="terracotta" className="mb-5 block">Mentoría</Eyebrow>
              <h2 className="font-serif font-light text-4xl md:text-5xl mb-7 leading-[1.08]">1:1 con mujeres que ya <span className="italic">recorrieron el camino.</span></h2>
              <div className="space-y-4 text-charcoal/80 text-[17px] leading-relaxed">
                <p>Nuestro programa de mentoría conecta miembros con mujeres senior en el sector que te interesa. Formato mensual, sesiones de 45 minutos, cuatro sesiones por ciclo.</p>
                <p>Aplican mujeres en transición de carrera, liderazgo emergente o expansión de proyecto propio. Sin costo para la miembra; las mentoras participan pro bono.</p>
              </div>
            </div>
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm">
              <img src={EDITORIAL_IMAGES.sponsor} alt="Sesión de mentoría" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </div>
        </Container>
      </section>

      {/* Directory concept */}
      <section className="bg-cream-dark py-20 border-t border-charcoal/5" data-testid="community-directory">
        <Container>
          <div className="text-center max-w-xl mx-auto mb-10">
            <Eyebrow tone="terracotta" className="mb-4 block">Directorio</Eyebrow>
            <h2 className="font-serif font-light text-3xl md:text-4xl leading-[1.08]">Cómo funciona.</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {DIRECTORY_CARDS.map((c, i) => (
              <div key={c.title} className="p-7 bg-white border border-charcoal/10 rounded-sm">
                <p className="font-serif text-3xl text-terracotta mb-4">0{i + 1}</p>
                <h3 className="font-serif text-xl text-charcoal mb-2.5 font-light">{c.title}</h3>
                <p className="text-sm text-charcoal/70 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Join form */}
      <section id="join" className="bg-charcoal text-cream py-24 md:py-32" data-testid="community-form">
        <Container>
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <Eyebrow tone="gold" className="mb-5 block">Únete a la comunidad</Eyebrow>
              <h2 className="font-serif font-light text-4xl md:text-5xl leading-[1.08]">Un solo registro. <span className="italic">Cero spam.</span></h2>
            </div>

            {done ? (
              <div data-testid="community-success" className="bg-charcoal-soft border border-gold/30 p-10 rounded-sm text-center">
                <p className="text-[10px] uppercase tracking-[0.28em] text-gold mb-3">Bienvenida</p>
                <h3 className="font-serif text-3xl font-light mb-3">Eres parte de la comunidad.</h3>
                <p className="text-cream/75">Recibirás nuestra próxima convocatoria en tu inbox y acceso anticipado a eventos.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <input type="text" tabIndex="-1" autoComplete="off" value={form.website} onChange={(e) => upd("website", e.target.value)} style={{ position: "absolute", left: "-9999px", width: 1, height: 1 }} aria-hidden="true" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="c-name" placeholder="Nombre completo *" value={form.name} onChange={(e) => upd("name", e.target.value)} className="w-full bg-transparent border border-cream/20 rounded-sm px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:border-gold focus:outline-none" required />
                  <input data-testid="c-email" type="email" placeholder="Email *" value={form.email} onChange={(e) => upd("email", e.target.value)} className="w-full bg-transparent border border-cream/20 rounded-sm px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:border-gold focus:outline-none" required />
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <input data-testid="c-city" placeholder="Ciudad" value={form.city} onChange={(e) => upd("city", e.target.value)} className="w-full bg-transparent border border-cream/20 rounded-sm px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:border-gold focus:outline-none" />
                  <select data-testid="c-industry" value={form.industry} onChange={(e) => upd("industry", e.target.value)} className="w-full bg-transparent border border-cream/20 rounded-sm px-4 py-3 text-sm text-cream focus:border-gold focus:outline-none">
                    <option value="" className="text-charcoal">Industria</option>
                    {INDUSTRIES.map((i) => (<option key={i} value={i} className="text-charcoal">{i}</option>))}
                  </select>
                  <select data-testid="c-role" value={form.role_interest} onChange={(e) => upd("role_interest", e.target.value)} className="w-full bg-transparent border border-cream/20 rounded-sm px-4 py-3 text-sm text-cream focus:border-gold focus:outline-none">
                    {ROLES.map((r) => (<option key={r} value={r} className="text-charcoal">{r}</option>))}
                  </select>
                </div>
                <button type="submit" data-testid="c-submit" disabled={submitting} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-terracotta text-cream px-7 py-3.5 text-sm font-medium hover:bg-[#A64826] transition-all duration-[400ms] disabled:opacity-60">
                  {submitting ? "Enviando…" : "Unirme a la comunidad"}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            )}
          </div>
        </Container>
      </section>
    </SiteLayout>
  );
}
